import BaseListNote from '@components/BaseListNote';
import { BaseTextField } from '@components/BaseTextField';
import PopUpBase from '@components/PopUpBase';
import { CellOfRow } from '@components/tables';
import { permitExemptHttpController } from '@controllers/index';
import { DEFAULT_PAGE_SIZE } from '@Core';
import { IGetListPermitExemptProps } from '@EcamModel/controllers/IPermitExemptHttpController';
import { ScopeType } from '@EcamModel/model/PermitExempt';
import useMedia from '@hooks/useMedia';
import { IPopUp } from '@hooks/usePopUp';
import { Button, Stack, TableCell, Typography } from '@mui/material';
import { FormattedPlates } from '@pages/permits-vehicle-eCam/components/ListDataPermitsVehicle';
import { FilterExemptState, mappingScopePermit } from '@pages/permits-vehicle-eCam/utils';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { useQuery } from 'react-query';

type Props = Omit<IPopUp, 'onConfirm'> & {};

export default function PopupReivewExemptionPermit(props: Props) {
    const { open, onClose } = props;
    const { isMobileSM, isMDOnly } = useMedia();

    const initFilter = {
        page: 1,
    } as FilterExemptState;

    const searchRef = useRef<HTMLInputElement | null>(null);
    const [filter, setFilter] = useState<Partial<FilterExemptState>>(initFilter);

    const getListExemptionPermit = async () => {
        const _filter: IGetListPermitExemptProps = {
            page: filter.page ?? 1,
            pageSize: DEFAULT_PAGE_SIZE,
            Plate: filter.searchValue,
        };

        const response = await permitExemptHttpController.list(_filter);
        return response;
    };

    const { data: listExemptPermits, isLoading } = useQuery(
        ['listExemptionPermit', filter.page, filter.searchValue],
        getListExemptionPermit,
        {
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 0,
        }
    );

    const handleOnchangeSearchPlate = useCallback(
        _.debounce((value: string) => {
            setFilter((prev) => ({ ...prev, searchValue: value, page: 1 }));
        }, 300),
        []
    );

    useEffect(() => {
        if (!open) return;
    }, [open]);

    return (
        <PopUpBase
            open={open}
            onClose={onClose}
            fixOverflow
            dialogProps={{
                maxWidth: 'lg',
                PaperProps: {
                    style: { minWidth: isMDOnly ? 500 : isMobileSM ? 350 : 700, minHeight: 380 },
                },
            }}
            title={<Typography variant="h4">Exemption list</Typography>}
            desc={
                <Stack spacing={2}>
                    <BaseTextField
                        placeholder={'Search by plate'}
                        inputRef={searchRef}
                        onChange={(e) => {
                            handleOnchangeSearchPlate(e.target.value);
                        }}
                        maxLength={50}
                        InputProps={{
                            endAdornment: (
                                <FiSearch
                                    color="disabled"
                                    size={16}
                                    style={{ color: 'rgba(0, 0, 0, 0.38)', cursor: 'pointer' }}
                                />
                            ),
                        }}
                    />

                    <BaseListNote
                        isLoading={isLoading}
                        paging={listExemptPermits}
                        tableHeaders={['No.', 'Plate', 'Scope', 'Name', 'Note']}
                        setPage={(page) => {
                            setFilter((prev) => ({ ...prev, page }));
                        }}
                        renderedItem={(item, index) => {
                            const serialNumber = (filter.page! - 1) * DEFAULT_PAGE_SIZE + index! + 1;
                            return (
                                <>
                                    <CellOfRow
                                        value={<Typography component={'span'}>{serialNumber}</Typography>}
                                        align="left"
                                    />
                                    <CellOfRow align="left" value={<FormattedPlates plates={item.VehicleExempts!} />} />
                                    <CellOfRow value={mappingScopePermit[item?.ScopeType]} align="left" />
                                    <CellOfRow
                                        value={
                                            item.ScopeType === ScopeType.Camera
                                                ? item.Camera?.Name ?? ''
                                                : item.ScopeType === ScopeType.Zone
                                                ? item.Zone?.PublicName ?? ''
                                                : item.Location?.Name ?? ''
                                        }
                                        align="left"
                                    />
                                    <TableCell align="left">
                                        <Typography>{item.Notes?.trim() ?? ''}</Typography>
                                    </TableCell>
                                </>
                            );
                        }}
                    />
                </Stack>
            }
            customActions={
                <Stack direction={'row'} width="100%" justifyContent="space-between">
                    <Button
                        sx={{ minWidth: 130 }}
                        variant="cancel"
                        onClick={() => {
                            props.onClose?.();
                        }}
                    >
                        Close
                    </Button>
                </Stack>
            }
            minWidthButton={150}
        />
    );
}
